import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowLeft, Send, Phone, Mail, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('E-mail é obrigatório');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('E-mail inválido');
      return false;
    }
    if (!formData.subject.trim()) {
      setError('Assunto é obrigatório');
      return false;
    }
    if (!formData.message.trim()) {
      setError('Mensagem é obrigatória');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Chamar API real para enviar email
      const response = await axios.post(`${API_URL}/api/contato/enviar`, {
        nome: formData.name,
        email: formData.email,
        telefone: formData.subject, // Usando subject como telefone temporariamente
        mensagem: formData.message
      }, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });

        toast({
          title: "Mensagem enviada!",
          description: "Recebemos sua mensagem e entraremos em contato em breve.",
          duration: 5000,
        });
      }

    } catch (err) {
      console.error('Erro ao enviar email:', err);
      setError(err.response?.data?.detail || 'Erro ao enviar mensagem. Tente novamente.');
      toast({
        title: "Erro no envio",
        description: err.response?.data?.detail || "Não foi possível enviar sua mensagem. Tente novamente.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-50 text-gray-900 py-12 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            
            <a 
              href="https://wa.me/5553981146431" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-bold"
            >
              <Phone className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-black mb-2 text-[rgb(25,42,86)]">CONTATO</h1>
            <p className="text-lg opacity-80 font-semibold">Entre em contato conosco</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Informações de contato */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-black text-[rgb(25,42,86)] mb-6">
                Fale Conosco
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Estamos sempre prontos para atender você. Entre em contato conosco para tirar dúvidas, 
                fazer sugestões ou solicitar informações sobre nossos leilões.
              </p>
            </div>

            {/* Cartões de contato */}
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-md border border-gray-200 hover:border-green-600 transition-colors duration-200 cursor-pointer">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Phone className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                  <div className="space-y-1">
                    <a 
                      href="https://wa.me/5553981146431" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
                    >
                      <span className="mr-2">📱</span>
                      (53) 98114-6431
                    </a>
                    <a 
                      href="https://wa.me/5553981146315" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
                    >
                      <span className="mr-2">📱</span>
                      (53) 98114-6315
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-md border border-gray-200 hover:border-[rgb(25,42,86)] transition-colors duration-200">
                <div className="p-3 bg-[rgb(25,42,86)]/10 rounded-lg">
                  <Mail className="h-6 w-6 text-[rgb(25,42,86)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">E-mail</h3>
                  <a href="mailto:contato@knorrleiloes.com.br" className="text-gray-600 hover:text-[rgb(25,42,86)] transition-colors">
                    contato@knorrleiloes.com.br
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-md border border-gray-200 hover:border-[rgb(25,42,86)] transition-colors duration-200">
                <div className="p-3 bg-[rgb(25,42,86)]/10 rounded-lg">
                  <MapPin className="h-6 w-6 text-[rgb(25,42,86)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Endereço</h3>
                  <p className="text-gray-600">R. Mal. Deodoro, 1221 - Centro</p>
                  <p className="text-gray-600">Pelotas - RS - 96020-220</p>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário de contato */}
          <div>
            <Card className="border-2 border-gray-200">
              <CardHeader className="bg-[rgb(25,42,86)] text-white rounded-t-lg">
                <CardTitle className="flex items-center justify-center space-x-2">
                  <Send className="h-5 w-5" />
                  <span>Envie sua Mensagem</span>
                </CardTitle>
              </CardHeader>

              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                      Nome *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Seu nome completo"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 border-gray-300 focus:border-[rgb(25,42,86)] focus:ring-[rgb(25,42,86)]"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                      E-mail *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 border-gray-300 focus:border-[rgb(25,42,86)] focus:ring-[rgb(25,42,86)]"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-sm font-medium text-gray-700">
                      Assunto *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="Assunto da sua mensagem"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="mt-1 border-gray-300 focus:border-[rgb(25,42,86)] focus:ring-[rgb(25,42,86)]"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                      Mensagem *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={6}
                      placeholder="Digite aqui sua mensagem..."
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-1 border-gray-300 focus:border-[rgb(25,42,86)] focus:ring-[rgb(25,42,86)]"
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <Alert className="border-[rgb(238,29,37)] text-[rgb(238,29,37)]">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="border-green-500 bg-green-50">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">
                        Mensagem enviada com sucesso! Entraremos em contato em breve.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 text-white font-bold py-3 transition-colors duration-200"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Enviando...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Send className="h-4 w-4" />
                        <span>Enviar Mensagem</span>
                      </div>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    * Campos obrigatórios
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Seção adicional */}
        <div className="mt-16 text-center bg-white p-8 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-2xl font-black text-[rgb(25,42,86)] mb-4">
            Pronto para participar dos nossos leilões?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Fale conosco, faça seu cadastro e venha fazer bons negócios.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 text-white px-8 py-3 font-bold"
            >
              Nossos Leilões
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white px-8 py-3 font-bold"
            >
              Fazer Login
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[rgb(25,42,86)] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white p-2 rounded">
              <img
                src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                alt="Knorr Leilões"
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
          <h3 className="text-2xl font-black mb-2">KNORR LEILÕES</h3>
          <p className="text-gray-300 mb-2">
            tradição, modernidade e eficiência
          </p>
          <p className="text-sm text-gray-400">
            © 2025 Knorr Leilões. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;